import { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom"; 
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { 
  Trash2, Edit, RefreshCw, Search, UserPlus, Activity, Eye, EyeOff, Laptop, WifiOff, ShieldCheck, Loader2, Clock, Crown, Plus, Network 
} from "lucide-react"; 
import { toast } from "sonner";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (bytes) => { 
  if (bytes === 0 || isNaN(bytes)) return '0 B'; 
  const k = 1024; 
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']; 
  const i = Math.floor(Math.log(bytes) / Math.log(k)); 
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]; 
};

export default function Users() {
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // --- STATE ---
  const [search, setSearch] = useState(queryParams.get("search") || "");
  const [profileFilter, setProfileFilter] = useState(queryParams.get("profile") || "all");
  const [statusFilter, setStatusFilter] = useState(queryParams.get("status") || "all");

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRegistered, setTotalRegistered] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // REFRESH STATE
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, type: null, data: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Registry State
  const [isRegistryOpen, setIsRegistryOpen] = useState(false);
  const [registryData, setRegistryData] = useState([]);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [newMac, setNewMac] = useState("");
  const [newMacComment, setNewMacComment] = useState("");
  const [isAddingMac, setIsAddingMac] = useState(false);
  const [unbindingMac, setUnbindingMac] = useState(null);

  // Inspect State
  const [isInspectOpen, setIsInspectOpen] = useState(false);
  const [inspectUser, setInspectUser] = useState(null);
  const [inspectHistory, setInspectHistory] = useState([]);
  const [inspectLoading, setInspectLoading] = useState(false);
  const [isBypassing, setIsBypassing] = useState(false);
  const [isUnbypassing, setIsUnbypassing] = useState(false);
  const [isKicking, setIsKicking] = useState(false);

  // Form Data
  const [formData, setFormData] = useState({ 
    username: "", 
    password: "", 
    temp_profile: "uprof-student", 
    server: "Student", 
    shared_users: "2" 
  });

  // --- FETCHERS ---
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = { 
        skip: (page - 1) * 20, limit: 20, 
        search, 
        profile: profileFilter,
        status: statusFilter 
      };
      const res = await axios.get(`${API}/hotspot/users`, { params });
      
      setUsers(res.data.users);
      setTotalRegistered(res.data.total_registered);
      setTotalPages(Math.ceil(res.data.filtered_count / 20));
      setSelectedUsers([]); 
    } catch (e) { 
      console.error("Fetch error", e); 
      toast.error("Failed to load user list");
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => {
    const qSearch = queryParams.get("search") || "";
    const qProfile = queryParams.get("profile") || "all";
    const qStatus = queryParams.get("status") || "all";
    setSearch(qSearch); setProfileFilter(qProfile); setStatusFilter(qStatus);
  }, [location.search]); 

  useEffect(() => { fetchUsers(); }, [page, search, profileFilter, statusFilter]);

  // --- HONEST REFRESH LOGIC ---
  const checkRefreshStatus = async () => {
      try {
          const res = await axios.get(`${API}/hotspot/sync-status`);
          if (res.data.is_syncing) {
              // Still working? Check again in 2 seconds
              setTimeout(checkRefreshStatus, 2000);
          } else {
              // FINISHED! 
              setIsRefreshing(false);
              fetchUsers(); // Pull the fresh data
              toast.success("User list refreshed successfully");
          }
      } catch (e) {
          setIsRefreshing(false);
          toast.error("Status check failed");
      }
  };

  const handleManualRefresh = async () => {
      setIsRefreshing(true);
      const toastId = toast.loading("Connecting to Router...");
      
      try {
          // 1. Trigger the background job
          await axios.post(`${API}/hotspot/sync`);
          
          // 2. Notify user that the job started
          toast.success("Refresh started...", { id: toastId });
          
          // 3. Start polling for completion
          checkRefreshStatus();
          
      } catch (e) {
          setIsRefreshing(false);
          toast.error("Could not start refresh", { id: toastId });
      }
  };

  // --- REGISTRY ACTIONS ---
  const openRegistry = async () => {
    setIsRegistryOpen(true);
    setRegistryLoading(true);
    try {
        const res = await axios.get(`${API}/hotspot/bypassed-registry`);
        setRegistryData(res.data);
    } catch (e) { toast.error("Registry Error"); } 
    finally { setRegistryLoading(false); }
  };

  const handleManualRegistryAdd = async () => {
      if(!newMac) return toast.error("Enter a MAC Address");
      setIsAddingMac(true);
      try {
          await axios.post(`${API}/hotspot/registry`, { 
              mac_address: newMac, comment: newMacComment || "Manual Entry" 
          });
          toast.success("Device Added");
          setNewMac(""); setNewMacComment("");
          await openRegistry(); 
      } catch (e) { toast.error("Failed to add MAC"); } finally { setIsAddingMac(false); }
  };

  const nukeBypass = async (mac) => {
      setUnbindingMac(mac); 
      try {
          await axios.post(`${API}/hotspot/users/unbind`, { mac_address: mac });
          toast.success("Unbound");
          await openRegistry(); 
          fetchUsers(); 
      } catch (e) { toast.error("Failed"); } finally { setUnbindingMac(null); }
  };

  // --- USER ACTIONS ---
  const handleSaveUser = async () => {
    setIsSaving(true);
    try {
      let finalProfile = formData.temp_profile;
      if (formData.temp_profile === 'vip') finalProfile = `vip-${formData.shared_users}`;

      const payload = {
          username: formData.username,
          password: formData.password,
          profile: finalProfile, 
          server: formData.server, 
          shared_users: parseInt(formData.shared_users) 
      };

      if (editingUser) {
        await axios.put(`${API}/hotspot/users/${editingUser.username}`, payload);
        toast.success("Updated");
      } else {
        await axios.post(`${API}/hotspot/users`, payload);
        toast.success("Created");
      }
      setIsModalOpen(false);
      fetchUsers(); 
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } 
    finally { setIsSaving(false); }
  };

  const initiateDelete = (username) => setDeleteConfirm({ isOpen: true, type: 'single', data: username });
  const initiateBulkDelete = () => setDeleteConfirm({ isOpen: true, type: 'bulk', data: selectedUsers });
  const executeDelete = async () => {
    setIsDeleting(true);
    try {
      if (deleteConfirm.type === 'single') await axios.delete(`${API}/hotspot/users/${deleteConfirm.data}`);
      else await axios.post(`${API}/hotspot/users/bulk-delete`, { usernames: deleteConfirm.data });
      toast.success("Deleted"); 
      setDeleteConfirm({ isOpen: false, type: null, data: null }); 
      fetchUsers(); 
    } catch (e) { toast.error("Delete failed"); } finally { setIsDeleting(false); }
  };

  // --- INSPECT ACTIONS ---
  const openInspectModal = async (username) => {
    setIsInspectOpen(true); setInspectLoading(true); setInspectUser(null); setInspectHistory([]);
    try {
      const d = await axios.get(`${API}/hotspot/users/${username}/details`);
      setInspectUser(d.data);
      const h = await axios.get(`${API}/hotspot/users/${username}/history`);
      setInspectHistory(h.data);
    } catch (e) { toast.error("Inspect Failed"); } finally { setInspectLoading(false); }
  };

  const handleBypassUser = async () => {
    setIsBypassing(true);
    try { await axios.post(`${API}/hotspot/users/bind`, { username: inspectUser.username, mac_address: inspectUser.mac_address }); toast.success("Bypassed"); setInspectUser(prev => ({ ...prev, is_bypassed: true })); fetchUsers(); } catch (e) { toast.error("Failed"); } finally { setIsBypassing(false); }
  };
  const handleUnbypassUser = async () => {
    setIsUnbypassing(true);
    try { await axios.post(`${API}/hotspot/users/unbind`, { username: inspectUser.username, mac_address: inspectUser.mac_address }); toast.success("Removed"); setInspectUser(prev => ({ ...prev, is_bypassed: false })); fetchUsers(); } catch (e) { toast.error("Failed"); } finally { setIsUnbypassing(false); }
  };
  const handleKickUser = async () => {
    setIsKicking(true);
    try { await axios.post(`${API}/hotspot/users/${inspectUser.username}/kick`); toast.success("Kicked"); setInspectUser(prev => ({ ...prev, status: 'Offline', uptime: '0s' })); fetchUsers(); } catch (e) { toast.error("Failed"); } finally { setIsKicking(false); }
  };

  // --- MODAL HELPERS ---
  const openAddModal = () => {
    setEditingUser(null);
    setFormData({ username: "", password: "", temp_profile: "uprof-student", server: "Student", shared_users: "2" });
    setShowPassword(false); setIsModalOpen(true);
  };
  const openEditModal = (user) => {
    setEditingUser(user);
    let uiProfile = user.profile; let uiLimit = "2"; 
    if (user.profile.startsWith("vip-")) { uiProfile = "vip"; uiLimit = user.profile.split("-")[1]; } 
    else if (user.profile === "uprof-employee") { uiProfile = "uprof-employee"; }
    setFormData({ username: user.username, password: "", temp_profile: uiProfile, server: user.server, shared_users: uiLimit });
    setShowPassword(false); setIsModalOpen(true);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-3xl font-black text-[#1a3d2e] tracking-tight">USER DATABASE</h2>
          <div className="flex items-center gap-2 mt-1">
             <Activity className="w-4 h-4 text-emerald-600 animate-pulse" />
             <p className="text-slate-500 font-medium">Live Registry</p>
             <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full font-bold ml-2">
               {totalRegistered.toLocaleString()} Registered
             </span>
          </div>
        </div>
        <div className="flex gap-2">
            {selectedUsers.length > 0 && (
              <Button onClick={initiateBulkDelete} variant="destructive">
                <Trash2 className="w-4 h-4 mr-2" /> Delete ({selectedUsers.length})
              </Button>
            )}
            <Button variant="outline" onClick={openRegistry} className="bg-indigo-50 text-indigo-700 border-indigo-100 hover:bg-indigo-100 font-bold">
              <ShieldCheck className="w-4 h-4 mr-2" /> Registry
            </Button>
            
            {/* HONEST REFRESH BUTTON */}
            <Button 
              variant="outline" 
              onClick={handleManualRefresh} 
              disabled={isRefreshing || loading} 
              className="bg-white hover:bg-slate-50 border-slate-200 font-bold text-slate-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${(isRefreshing || loading) ? 'animate-spin' : ''}`} />
              {isRefreshing ? "Refreshing List..." : "Refresh List"}
            </Button>

            <Button onClick={openAddModal} className="bg-[#1a3d2e] hover:bg-[#254f3b] text-white shadow-md font-bold">
              <UserPlus className="w-4 h-4 mr-2" /> Add User
            </Button>
        </div>
      </div>

      {/* FILTERS */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-slate-50 border-slate-200 focus:bg-white transition-all focus:ring-[#1a3d2e]" />
        </div>
        <Select value={profileFilter} onValueChange={setProfileFilter}>
          <SelectTrigger className="w-[180px] bg-slate-50 border-slate-200"><SelectValue placeholder="All Profiles" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Profiles</SelectItem><SelectItem value="uprof-student">Student</SelectItem><SelectItem value="uprof-employee">Employee</SelectItem><SelectItem value="vip">VIP</SelectItem></SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-slate-50 border-slate-200"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="online">Online</SelectItem><SelectItem value="offline">Offline</SelectItem></SelectContent>
        </Select>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-[#1a3d2e]">
            <TableRow className="hover:bg-[#1a3d2e] border-b-0">
              <TableHead className="w-[40px] text-center"><input type="checkbox" className="accent-emerald-500 w-4 h-4" onChange={() => selectedUsers.length === users.length ? setSelectedUsers([]) : setSelectedUsers(users.map(u => u.username))} checked={users.length > 0 && selectedUsers.length === users.length} /></TableHead>
              <TableHead className="text-white font-bold h-12 uppercase text-xs tracking-wider">User</TableHead>
              <TableHead className="text-white font-bold h-12 uppercase text-xs tracking-wider">Profile</TableHead>
              <TableHead className="text-white font-bold h-12 uppercase text-xs tracking-wider text-center">Active Devices</TableHead> 
              <TableHead className="text-white font-bold h-12 uppercase text-xs tracking-wider">Status</TableHead>
              <TableHead className="text-white font-bold h-12 text-right uppercase text-xs tracking-wider pr-6">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? <TableRow><TableCell colSpan={6} className="h-40 text-center text-slate-500">No users found.</TableCell></TableRow> : users.map((user) => (
                <TableRow key={user.id} className={`hover:bg-slate-50 border-slate-100 group ${selectedUsers.includes(user.username) ? 'bg-emerald-50/50' : ''}`}>
                  <TableCell className="text-center"><input type="checkbox" className="accent-emerald-500 w-4 h-4" checked={selectedUsers.includes(user.username)} onChange={() => selectedUsers.includes(user.username) ? setSelectedUsers(selectedUsers.filter(u => u !== user.username)) : setSelectedUsers([...selectedUsers, user.username])} /></TableCell>
                  <TableCell><span className="font-bold text-slate-800 text-sm flex items-center gap-2">{user.username} {user.profile.startsWith('vip') && <Crown className="w-3 h-3 text-purple-600" />}</span></TableCell>
                  <TableCell><span className={`w-fit px-2 py-0.5 rounded text-[10px] uppercase font-bold border mb-1 ${user.profile.startsWith('vip') ? 'bg-purple-50 text-purple-700 border-purple-100' : 'bg-teal-50 text-teal-700 border-teal-100'}`}>{user.profile.startsWith('vip') ? 'VIP' : user.profile.replace('uprof-', '')}</span></TableCell>
                  
                  {/* CLEAN PILL CELL */}
                  <TableCell className="text-center">
                    <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-[10px] font-bold ${user.active_count > 0 ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' : 'text-slate-400 bg-slate-50 border border-slate-100'}`}>
                        {user.active_count || 0} Active
                    </span>
                  </TableCell>

                  <TableCell>{user.is_online ? <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Online</span> : <span className="text-xs font-bold text-slate-400 uppercase tracking-wide flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-slate-300"></span> Offline</span>}</TableCell>
                  <TableCell className="text-right pr-6"><div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity"><Button variant="ghost" size="icon" onClick={() => openInspectModal(user.username)} className="h-8 w-8 text-emerald-600 hover:bg-emerald-50"><Eye className="w-4 h-4" /></Button><Button variant="ghost" size="icon" onClick={() => openEditModal(user)} className="h-8 w-8 text-blue-600 hover:bg-blue-50"><Edit className="w-4 h-4" /></Button><Button variant="ghost" size="icon" onClick={() => initiateDelete(user.username)} className="h-8 w-8 text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button></div></TableCell>
                </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

{/* REGISTRY MODAL */}
      <Dialog open={isRegistryOpen} onOpenChange={setIsRegistryOpen}>
          <DialogContent className="max-w-lg p-0 overflow-hidden border-0 shadow-2xl transition-all">
              
              <div className="bg-[#1a3d2e] p-4 flex justify-between items-center text-white">
                  <div>
                    <h3 className="text-lg font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Bypass Registry</h3>
                    <p className="text-emerald-200 text-[10px] mt-0.5">Manual MAC Address Bindings</p>
                  </div>
                  <Button variant="secondary" size="sm" onClick={openRegistry} disabled={registryLoading} className="text-[#1a3d2e] bg-white h-8 text-xs font-bold">
                    <RefreshCw className={`w-3 h-3 mr-2 ${registryLoading ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
              </div>
              
              <div className="p-4 bg-white border-b border-slate-100 flex justify-center">
                  <div className="flex flex-col sm:flex-row gap-2 items-end w-full justify-center">
                    <div className="space-y-1">
                        <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">MAC Address</Label>
                        <Input placeholder="XX:XX:XX:XX:XX:XX" value={newMac} onChange={(e) => setNewMac(e.target.value.toUpperCase())} className="font-mono uppercase h-9 text-sm w-[180px]" />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Description</Label>
                        <Input placeholder="e.g. Printer" value={newMacComment} onChange={(e) => setNewMacComment(e.target.value)} className="h-9 text-sm w-[160px]" />
                    </div>
                    <Button onClick={handleManualRegistryAdd} disabled={isAddingMac} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-9 px-4">
                        {isAddingMac ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    </Button>
                  </div>
              </div>

              <div className="max-h-[50vh] overflow-y-auto">
                  <Table>
                      <TableHeader className="bg-slate-50 sticky top-0 z-10">
                        <TableRow className="h-9 border-b border-slate-100">
                          <TableHead className="font-bold text-slate-500 uppercase text-[10px] pl-8">MAC Address</TableHead>
                          <TableHead className="font-bold text-slate-500 uppercase text-[10px]">Comment</TableHead>
                          <TableHead className="text-right font-bold text-slate-500 uppercase text-[10px] pr-8">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                          {registryLoading ? (
                             <TableRow><TableCell colSpan={3} className="h-24 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-600" /></TableCell></TableRow>
                          ) : registryData.length === 0 ? (
                             <TableRow><TableCell colSpan={3} className="h-24 text-center text-slate-400 text-xs italic">No devices found.</TableCell></TableRow>
                          ) : (
                             registryData.map((item, i) => (
                               <TableRow key={i} className="hover:bg-slate-50 border-b border-slate-50 h-10">
                                   <TableCell className="font-mono text-xs font-medium text-slate-700 pl-8">{item.mac_address}</TableCell>
                                   <TableCell className="text-xs text-slate-500 truncate max-w-[150px]">{item.comment}</TableCell>
                                   <TableCell className="text-right pr-8">
                                      <button className="text-[10px] font-bold text-red-400 hover:text-red-600 hover:underline disabled:opacity-50 transition-colors uppercase tracking-wider" disabled={unbindingMac === item.mac_address} onClick={() => nukeBypass(item.mac_address)}>
                                        {unbindingMac === item.mac_address ? "..." : "UNBIND"}
                                      </button>
                                   </TableCell>
                               </TableRow>
                             ))
                          )}
                      </TableBody>
                  </Table>
              </div>
          </DialogContent>
      </Dialog>
      
      <Dialog open={deleteConfirm.isOpen} onOpenChange={(open) => !open && !isDeleting && setDeleteConfirm({...deleteConfirm, isOpen: false})}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader><DialogTitle className="text-xl flex items-center gap-2 text-red-600"><Trash2 className="h-5 w-5" /> Confirm Deletion</DialogTitle><DialogDescription className="pt-2">This action cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0"><Button variant="outline" disabled={isDeleting} onClick={() => setDeleteConfirm({...deleteConfirm, isOpen: false})}>Cancel</Button><Button variant="destructive" disabled={isDeleting} onClick={executeDelete}>{isDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Confirm Delete"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle className="text-[#1a3d2e] font-bold text-xl">{editingUser ? "Edit User" : "Provision New User"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4"><Label className="text-right">Username</Label><Input value={formData.username} onChange={(e) => setFormData({...formData, username: e.target.value})} className="col-span-3 border-slate-200" /></div>
            <div className="grid grid-cols-4 items-center gap-4"><Label className="text-right">Password</Label><div className="col-span-3 relative"><Input value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} type={showPassword ? "text" : "password"} placeholder={editingUser ? "(Unchanged)" : "Password"} className="pr-10 border-slate-200" /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div></div>
            <div className="grid grid-cols-4 items-center gap-4"><Label className="text-right">Profile</Label><Select value={formData.temp_profile} onValueChange={(val) => { let autoServer = "Student"; if (val === "uprof-employee" || val === "vip") autoServer = "Employee"; setFormData({ ...formData, temp_profile: val, server: autoServer }); }}><SelectTrigger className="col-span-3 border-slate-200"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="uprof-student">Student</SelectItem><SelectItem value="uprof-employee">Employee</SelectItem><SelectItem value="vip">VIP User</SelectItem></SelectContent></Select></div>
            {formData.temp_profile === 'vip' && (<div className="grid grid-cols-4 items-center gap-4 animate-in slide-in-from-top-2"><Label className="text-right font-bold text-purple-700">Limit</Label><div className="col-span-3"><Select value={String(formData.shared_users)} onValueChange={(val) => setFormData({...formData, shared_users: val})}><SelectTrigger className="border-purple-200 bg-purple-50 text-purple-900 font-bold"><SelectValue /></SelectTrigger><SelectContent>{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (<SelectItem key={num} value={String(num)}>{num} Devices</SelectItem>))}</SelectContent></Select></div></div>)}
          </div>
          <DialogFooter><Button onClick={handleSaveUser} disabled={isSaving} className="bg-[#1a3d2e] hover:bg-[#254f3b] w-full font-bold shadow-md">{isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (editingUser ? "Save Changes" : "Create User")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={isInspectOpen} onOpenChange={setIsInspectOpen}>
        <DialogContent className="p-0 border-0 shadow-2xl overflow-hidden max-w-md">
           <div className="bg-[#1a3d2e] p-6 text-white text-center relative"><h3 className="text-xl font-black tracking-tight">{inspectUser?.username || "Loading..."}</h3></div>
           <div className="p-6 bg-white space-y-4">
             {inspectLoading ? <div className="py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div> : (
               <>
                 <div className="grid grid-cols-2 gap-3 text-center">
                    <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100"><p className="text-[10px] uppercase font-bold text-emerald-600 mb-1">Total Data</p><p className="text-xl font-black text-emerald-900">{formatBytes(inspectUser?.total_bytes)}</p></div>
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-100"><p className="text-[10px] uppercase font-bold text-slate-400 mb-1">Uptime</p><p className="text-lg font-bold text-slate-700">{inspectUser?.uptime || "-"}</p></div>
                 </div>
                 <div className="space-y-1 bg-slate-50 p-4 rounded-xl text-sm border border-slate-100"><div className="flex justify-between items-center"><span className="text-slate-400 font-medium flex gap-2"><Laptop className="w-4 h-4"/> MAC</span><span className="font-mono font-bold text-slate-700">{inspectUser?.mac_address || "-"}</span></div><div className="flex justify-between items-center mt-2"><span className="text-slate-400 font-medium flex gap-2"><Network className="w-4 h-4"/> IP</span><span className="font-mono font-bold text-slate-700">{inspectUser?.ip_address || "-"}</span></div></div>
                 <div className="space-y-2 pt-2">{inspectUser?.is_bypassed ? <Button onClick={handleUnbypassUser} disabled={isUnbypassing} variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 font-bold h-11">{isUnbypassing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <WifiOff className="w-4 h-4 mr-2" />} Remove Bypass</Button> : <Button onClick={handleBypassUser} disabled={isBypassing} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11">{isBypassing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />} Bypass Login</Button>}{inspectUser?.status === 'Online' && !inspectUser?.is_bypassed && <Button onClick={handleKickUser} disabled={isKicking} variant="destructive" className="w-full bg-red-50 text-red-600 border border-red-100 hover:bg-red-600 hover:text-white transition-colors h-11">{isKicking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <WifiOff className="w-4 h-4 mr-2" />} Disconnect Session</Button>}</div>
                 <div className="mt-6 border-t border-slate-100 pt-4"><p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2"><Clock className="w-3 h-3" /> Recent Session History</p><div className="space-y-3">{inspectHistory.length > 0 ? inspectHistory.map((h, i) => (<div key={i} className="flex justify-between items-center text-xs p-2 bg-slate-50 rounded-lg"><div><p className="font-bold text-slate-700">{h.date}</p><p className="text-slate-400">{h.duration} duration</p></div><span className="font-mono text-emerald-600 font-bold">{formatBytes(h.data)}</span></div>)) : <p className="text-center text-[10px] text-slate-400 italic py-2">No previous records found.</p>}</div></div>
               </>
             )}
           </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}